import {
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  onAuthStateChanged,
  type User,
} from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';

import { auth, firestore } from './config';
import { USE_API, api, setToken, getToken } from '@/services/api/client';
import type { Usuario, Rol } from '@/types/domain';

// Cache del perfil cuando usamos API (el login lo devuelve directo)
let apiPerfilCache: Usuario | null = null;

export async function signIn(email: string, password: string) {
  if (USE_API) {
    const { token, perfil } = await api.post('/auth/login', { email: email.trim(), password });
    setToken(token);
    apiPerfilCache = perfil;
    return { uid: perfil.uid } as unknown as User;
  }
  const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
  return cred.user;
}

export async function signOut() {
  if (USE_API) {
    setToken(null);
    apiPerfilCache = null;
    return;
  }
  await fbSignOut(auth);
}

export function onAuthChange(cb: (user: User | null) => void) {
  if (USE_API) {
    // No hay listener persistente: si hay token, validamos contra /me.
    const token = getToken();
    if (!token) {
      cb(null);
    } else {
      api
        .get('/auth/me')
        .then((res) => {
          apiPerfilCache = res.perfil;
          cb({ uid: res.perfil.uid } as unknown as User);
        })
        .catch(() => {
          setToken(null);
          cb(null);
        });
    }
    return () => {};
  }
  return onAuthStateChanged(auth, cb);
}

export async function getUserDoc(uid: string): Promise<Usuario | null> {
  if (USE_API) {
    if (apiPerfilCache && apiPerfilCache.uid === uid) return apiPerfilCache;
    const res = await api.get('/auth/me');
    apiPerfilCache = res.perfil;
    return res.perfil;
  }
  const ref = doc(firestore, 'users', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { uid, ...(snap.data() as Omit<Usuario, 'uid'>) };
}

export async function ensureUserDoc(user: User, defaultRol: Rol = 'doctor') {
  if (USE_API) return;
  const ref = doc(firestore, 'users', user.uid);
  const snap = await getDoc(ref);
  if (snap.exists()) return;
  await setDoc(ref, {
    email: user.email ?? '',
    nombre: user.displayName ?? user.email?.split('@')[0] ?? 'Usuario',
    rol: defaultRol,
    activo: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}
